<template>
    <div class="flex flex-col h-auto min-h-screen max-h-screen w-full bg-green-90">
        <div class="w-full px-5 md:px-10 lg:px-32 xl:px-64 pt-4 pb-3 bg-green-400 text-grey-600 text-3xl sm:text-4xl shadow-2xl">
			<a @click="goBack" @keypress.enter="goBack" tabindex="0" class="cursor-pointer">
				<svg class="inline-block align-middle mr-1 -mt-1" width="30" height="30" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M19.375 23.25L11.625 15.5L19.375 7.75" class="stroke-current" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
			</a>
			
			Rulebook
		</div>
		<div class="px-5 md:px-10 lg:px-32 xl:px-64 py-5 font-body text-green-1000 text-xl overflow-x-hidden overflow-y-auto">
			<error class="rounded-lg" colour="red" icon="triangle_error" title="Health warning" body="This challenge requires a lot of walking. Please do not attempt it if you are feeling faint or unwell, or are physically unfit." />
			
			<div class="md:flex mt-6">
				<div class="md:pr-12 w-full md:w-1/3 lg:w-1/4 pr-24 md:pl-4">
					<img class="block w-full" alt="" src="/assets/img/rulebook/maps.png" />
				</div>
				<div class="md:-ml-3 md:w-2/3 lg:w-3/4">
					<p class="mt-4 md:mt-0 font-body text-green-1000 text-xl sm:text-2xl leading-none text-justify">
						No browsers, maps, or route-planning apps can be used during the
						challenge. You can however look at maps on signs and bus stops
						along your journey, or ask strangers for help.
					</p>

					<div class="md:flex mt-2">
						<div class="md:pr-6 md:w-1/2">
							<h4 class="text-green-700 text-2xl sm:text-3xl font-sans -mb-1">You can:</h4>

							<ul class="font-body text-green-1000 text-xl sm:text-2xl">
								<li class="list-item"><span class="list-symbol bg-green-700"></span> look at maps on signs and bus stops</li>
								<li class="list-item"><span class="list-symbol bg-green-700"></span> ask strangers for help</li>
								<li class="list-item"><span class="list-symbol bg-green-700"></span> use your phone for essential functions like calling and texting</li>
							</ul>
						</div>
						<div class="md:w-1/2">
							<h4 class="text-red-600 text-2xl sm:text-3xl font-sans -mb-1">You can't:</h4>

							<ul class="font-body text-red-1000 text-xl sm:text-2xl">
								<li class="list-item"><span class="list-symbol bg-red-600"></span> use paper or digital maps</li>
								<li class="list-item"><span class="list-symbol bg-red-600"></span> write down instructions from a stranger</li>
								<li class="list-item"><span class="list-symbol bg-red-600"></span> copy down a map from a sign or bus stop</li>
								<li class="list-item"><span class="list-symbol bg-red-600"></span> use your phone to look up directions</li>
								<li class="list-item"><span class="list-symbol bg-red-600"></span> ask a stranger to look up directions on their phone</li>
							</ul>
						</div>
					</div>
				</div>
			</div>

			<div class="md:flex mt-10">
				<div class="md:pr-12 w-full md:w-1/3 lg:w-1/4 pr-24 md:pl-4">
					<img class="block w-full" alt="" src="/assets/img/rulebook/transport.png" />
				</div>
				<div class="md:-ml-3 md:w-2/3 lg:w-3/4">
					<p class="mt-4 md:mt-0 font-body text-green-1000 text-xl sm:text-2xl leading-none text-justify">
						The use of public (buses, trains, etc) or private (bikes, taxis,
						cars, etc) transport is not allowed during the challenge. The
						challenge may be marked as invalid if we detect that you used
						some form of transport.
					</p>

					<div class="md:flex mt-2">
						<div class="md:pr-6 md:w-1/2">
							<h4 class="text-green-700 text-2xl sm:text-3xl font-sans -mb-1">You can:</h4>

							<ul class="font-body text-green-1000 text-xl sm:text-2xl">
								<li class="list-item"><span class="list-symbol bg-green-700"></span> run, but a moderator may need to verify your attempt before you can share it</li>
								<li class="list-item"><span class="list-symbol bg-green-700"></span> be mindful of other pedestrians, cars and cyclists when completing the challenge</li>
							</ul>
						</div>
						<div class="md:w-1/2">
							<h4 class="text-red-600 text-2xl sm:text-3xl font-sans -mb-1">You can't:</h4>

							<ul class="font-body text-red-1000 text-xl sm:text-2xl">
								<li class="list-item"><span class="list-symbol bg-red-600"></span> use public transport (e.g. buses, trains, trams, ferries)</li>
								<li class="list-item"><span class="list-symbol bg-red-600"></span> use private transport (e.g. cars, taxis, bicycles)</li>
							</ul>
						</div>
					</div>
				</div>
			</div>

			<div class="md:flex mt-10 mb-4">
				<div class="md:pr-12 w-full md:w-1/3 lg:w-1/4 pr-24 md:pl-4">
					<img class="block w-full" alt="" src="/assets/img/rulebook/property.png" />
				</div>
				<div class="md:-ml-3 md:w-2/3 lg:w-3/4">
					<p class="mt-4 md:mt-0 font-body text-green-1000 text-xl sm:text-2xl leading-none text-justify">
						Please avoid trespassing on private property while completing the challenge.
						There is no need to enter any areas restricted to staff or paying customers
						as simply standing near those areas will be counted as a visit.
					</p>

					<div class="md:flex mt-2">
						<div class="md:pr-6 md:w-1/2">
							<h4 class="text-green-700 text-2xl sm:text-3xl font-sans -mb-1">You can:</h4>

							<ul class="font-body text-green-1000 text-xl sm:text-2xl">
								<li class="list-item"><span class="list-symbol bg-green-700"></span> stand next to checkpoints that have restricted access to be counted as a visit</li>
								<li class="list-item"><span class="list-symbol bg-green-700"></span> report to us (in the challenge options) when it is not possible to enter an area</li>
							</ul>
						</div>
						<div class="md:w-1/2">
							<h4 class="text-red-600 text-2xl sm:text-3xl font-sans -mb-1">You can't:</h4>

							<ul class="font-body text-red-1000 text-xl sm:text-2xl">
								<li class="list-item"><span class="list-symbol bg-red-600"></span> go into areas where access to the public is restricted</li>
								<li class="list-item"><span class="list-symbol bg-red-600"></span> use the challenge as an excuse to gain entry to a paid area for free</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
    </div>
</template>

<style>
.list-item {
	position: relative;
	padding-left: 25px;
	line-height: 1.1;
	margin-top: 10px;
}

.list-symbol {
	display: block;
	position: absolute;
	top: 5px;
	left: 0;
	height: 0.7rem;
	width: 0.7rem;
	margin-right: 5px;
	transform: rotate(30deg);
}
</style>

<script>
import ErrorBox from '../components/ErrorBox.vue';

export default {
    name: 'Rulebook',
    path: '/rulebook',
	components: {
		'error': ErrorBox
	},
	methods: {
        goBack() {
			if (window.history.length == 1) {
				this.$router.replace("/");
				return;
			}
			
			this.$router.go(-1);
		}
    }
}
</script>